import axios, { AxiosInstance } from 'axios';
import { logger } from '../utils/logger';


/**
 * Résout le tenant par défaut UNE fois, et le met en mémoire.
 *
 * Appelée seulement quand rien n'est encore connu — au tout premier écran d'un
 * visiteur. Requête axios NUE : passer par le client applicatif rappellerait
 * cet intercepteur, donc une récursion sans fin. Un échec n'est pas mémorisé,
 * pour qu'une coupure réseau passagère n'interdise pas la tentative suivante.
 */
let amorcageTenant: Promise<string | null> | null = null;
function resoudreTenantParDefaut(): Promise<string | null> {
  if (!amorcageTenant) {
    const base = (import.meta as any).env?.VITE_API_URL || '/api';
    amorcageTenant = axios
      .get(`${base}/tenants/slug/folies`)
      .then((r) => {
        try {
          localStorage.setItem('tenant', JSON.stringify(r.data));
        } catch {
          /* stockage indisponible (navigation privée) : l'identifiant suffit */
        }
        return (r.data?.id as string) || null;
      })
      .catch(() => {
        amorcageTenant = null;
        return null;
      });
  }
  return amorcageTenant;
}

// Timeout court pour les appels de session/panier (ping, acquire, save, release).
// Ces appels sont rapides (<1s en temps normal) ; sans borne, une coupure réseau les
// laissait pendre ~60s (timeout TCP OS) → POS figé au changement de vendeur, détection
// offline retardée. 4s = échec rapide → bascule offline en ~8-12s (3 pings).
const SESSION_TIMEOUT_MS = 4000;

// Types
export interface ParsedLine {
  name: string;
  product_code?: string;
  barcode?: string;
  quantity?: number;
  unit?: string;
  unit_price_ttc_eur?: number;
  unit_price_ht_eur?: number;
  tva?: number;
  origin_country?: string;
  category_hint?: string;
}

export interface ParsedDocument {
  supplier: string;
  document_type: 'BL' | 'INVOICE' | 'ORDER';
  document_number?: string;
  document_date?: string;
  currency: 'EUR';
  lines: ParsedLine[];
  confidence_score: number;
  parsing_notes?: string;
}

export interface ProductImage {
  id: string;
  tenant_id: string;
  product_id: string;
  url: string;
  thumbnail_url: string | null;
  position: number;
  type: 'PRODUCT' | 'LABEL';
  label_text: string | null;
  alt_text: string | null;
  created_at: string;
}

export interface Delivery {
  id: string;
  supplier: string;
  document_type: string;
  document_number?: string;
  document_date?: string;
  parsing_method: 'ai' | 'script' | 'manual';
  ai_confidence_score?: number;
  status: 'pending' | 'approved' | 'applied';
  created_at: string;
  // Document source (traçabilité AFSCA)
  document_url?: string;
  document_filename?: string;
  document_mime_type?: string;
  // Parsing email (confirmation de commande)
  source_type?: 'file' | 'email_text' | 'manual';
  order_reference?: string;
}

export interface DeliveryLine {
  id: string;
  delivery_id: string;
  raw_name: string;
  normalized_name?: string;
  product_code?: string;
  barcode?: string;
  quantity?: number;
  unit?: string;
  unit_price_ttc_cents?: number;
  tax_rate?: number;
  origin_country?: string;
  organic_certifier_name?: string;
  organic_certifier_code?: string;
  category_hint?: string;
  default_expiry_date?: string;
  supplier_lot_number?: string;
  producer_name?: string;
  // Réception (flux ORDER)
  ordered_quantity?: number | null;
  received_quantity?: number | null;
  reception_status?: 'expected' | 'received' | 'partial' | 'missing' | 'extra';
  label_photo_url?: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'merged';
  /** Motif du refus (rejectLine l'écrit ici). Renvoyé par le serveur, jamais typé jusqu'ici. */
  notes?: string | null;
  matched_product_id?: string;
  matched_product_name?: string;
  matched_product_image_url?: string;
  matched_product_description?: string;
  matched_product_barcode?: string;
  matched_product_unit?: string | null;
  matched_product_is_weighted?: boolean | null;
  /** Prix réellement pratiqué en caisse (table prices), pas celui du lot. */
  matched_product_price_cents?: number | null;
  /** Prix d'achat habituel (TTC, médiane des 5 derniers lots) — repère du prix d'un colis lu au kilo. */
  matched_product_usual_purchase_cents?: number | null;
  matched_category_name?: string;
  similar_products?: ProductSuggestion[];
}

export interface ProductSuggestion {
  id: string;
  name: string;
  code: string | null;
  barcode: string | null;
  category_id: string | null;
  category_name: string | null;
  origin_country: string | null;
  bio: boolean;
  image_url: string | null;
  description: string | null;
  price_cents: number | null;
  stock_quantity: number | null;
  similarity_score: number;
}

/** Client déjà vu en précommande — proposé à la saisie du nom. */
export interface PreorderCustomerSuggestion {
  name: string;
  phone: string | null;
  orders_count: number;
  last_pickup_date: string | null;
}

export interface DeliveryConflictWarning {
  line_id: string;
  line_name: string;
  warning_type: 'similar_product' | 'duplicate_barcode' | 'duplicate_code';
  message: string;
  similar_products?: ProductSuggestion[];
}

export interface DeliveryConflictCheck {
  hasWarnings: boolean;
  warnings: DeliveryConflictWarning[];
  summary: {
    total_lines: number;
    lines_with_warnings: number;
    new_products: number;
    existing_products: number;
  };
}

export interface DeliveryApplyResult {
  status?: 'warnings' | 'success';
  message?: string;
  hasWarnings?: boolean;
  warnings?: DeliveryConflictWarning[];
  summary?: DeliveryConflictCheck['summary'];
  // Résultat de l'application réussie
  applied?: boolean;
  products_created?: number;
  products_updated?: number;
  lots_created?: number;
}

export interface UploadParseResponse {
  success: boolean;
  job_id: string;
  message: string;
  status_url: string;
}

export interface JobStatusResponse {
  id: string;
  name: string;
  data: {
    tenantId: string;
    filename: string;
    mimeType: string;
    supplierHint?: string;
  };
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress: number;
  result?: {
    success: boolean;
    delivery_id: string;
    supplier: string;
    document_type: string;
    line_count: number;
    confidence_score: number;
    enrichment_stats: {
      lines_with_category: number;
    };
    warning?: string;
  };
  error?: string;
  attemptsMade: number;
  processedOn?: number;
  finishedOn?: number;
}

class ApiClient {
  /** Garde-fou : une seule redirection /login par expiration de session (pas 50) */
  static sessionExpiredRedirected = false;

  private client: AxiosInstance;
  private currentTenantId: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
    });

    // Intercepteur pour ajouter le token JWT et le tenant ID
    this.client.interceptors.request.use(async (config) => {
      // Envoi de fichier : SUPPRIMER le Content-Type pour que le navigateur pose
      // lui-même « multipart/form-data; boundary=… ». Sans le boundary, multer ne
      // peut pas découper le formulaire → « Unexpected end of form » ou 400
      // (constaté 30/07/2026 sur la photo d'étiquette en réception).
      // À défaut, le défaut 'application/json' de l'instance s'appliquerait.
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers && typeof (config.headers as any).delete === 'function') {
          (config.headers as any).delete('Content-Type');
        } else if (config.headers) {
          delete (config.headers as any)['Content-Type'];
        }
      }

      // CONTEXTE par page : /shop (+ ticket public /sales) = espace CLIENT,
      // tout le reste (dashboard, POS) = espace STAFF. Le jeton est choisi par
      // contexte — un customer_token présent ne doit JAMAIS primer sur la
      // session staff hors de l'eshop (sinon : vendeur qui a fait ses courses
      // sur l'eshop dans le même navigateur → 401 sur tout le POS/dashboard).
      const customerToken = localStorage.getItem('customer_token');
      const staffToken = localStorage.getItem('auth_token');
      const isShopContext =
        window.location.pathname.startsWith('/shop') ||
        window.location.pathname.startsWith('/sales/');
      const token = isShopContext
        ? customerToken || staffToken
        : staffToken || customerToken;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Tenant ID : currentTenantId, puis le tenant du contexte courant
      let tenantId = this.currentTenantId;

      if (!tenantId) {
        const customerTenantId = localStorage.getItem('customer_tenant_id');
        let staffTenantId: string | undefined;
        const tenantData = localStorage.getItem('tenant');
        if (tenantData) {
          try {
            staffTenantId = JSON.parse(tenantData)?.id;
          } catch (e) {
            logger.error('[ApiClient] Failed to parse tenant data:', e);
          }
        }
        tenantId = isShopContext
          ? customerTenantId || staffTenantId || null
          : staffTenantId || customerTenantId || null;
      }

      // Première visite : rien en mémoire, le tenant n'est pas encore résolu.
      // On l'ATTEND au lieu de partir sans en-tête — sinon l'API répond 400 et
      // le catalogue s'affiche « Aucun produit trouvé » à un client qui arrive
      // d'un lien Facebook ou d'un QR de flyer. En navigation normale, le
      // rechargement provoqué par l'installation du service worker masquait le
      // défaut ; en navigation privée Safari, où aucun service worker ne
      // s'enregistre, la boutique restait vide.
      if (!tenantId) {
        tenantId = await resoudreTenantParDefaut();
      }

      if (tenantId) {
        config.headers['x-tenant-id'] = tenantId;
      } else {
        logger.warn('[ApiClient] ⚠️ No tenant ID available for request:', config.method?.toUpperCase(), config.url);
      }

      return config;
    });

    // Intercepteur pour gérer les erreurs (retry silencieux sur 429)
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;
        if (error.response?.status === 429 && (!config._retryCount || config._retryCount < 3)) {
          config._retryCount = (config._retryCount || 0) + 1;
          const delay = config._retryCount * 1000; // 1s, 2s, 3s
          logger.warn(`429 Too Many Requests - retry ${config._retryCount}/3 in ${delay}ms`);
          await new Promise(r => setTimeout(r, delay));
          return this.client(config);
        }
        if (error.response?.status === 401) {
          // N'effacer QUE le jeton réellement utilisé par la requête en échec.
          // (Avant : les DEUX jetons étaient supprimés → un 401 côté client
          // eshop tuait aussi la session staff, et inversement.)
          const usedToken = String(config?.headers?.Authorization || '').replace('Bearer ', '');
          if (usedToken && usedToken === localStorage.getItem('customer_token')) {
            localStorage.removeItem('customer_token');
            logger.warn('401 - customer_token invalide ou expiré, supprimé');
          } else if (usedToken && usedToken === localStorage.getItem('auth_token')) {
            localStorage.removeItem('auth_token');
            logger.warn('401 - auth_token (staff) invalide ou expiré, supprimé');
          } else {
            logger.warn('401 Unauthorized - requête sans jeton connu');
          }

          // Session expirée → retour au login, au lieu de laisser la page
          // morte accumuler des 401. Exclusions :
          // - flux d'authentification (mauvais mot de passe = 401 attendu)
          // - eshop (navigation publique, CustomerContext gère)
          // - écran client (page publique passive)
          // - déjà sur /login
          const path = window.location.pathname;
          const reqUrl: string = config?.url || '';
          const isAuthFlow = reqUrl.includes('/auth/') || reqUrl.includes('/field-auth');
          const isExcludedPage =
            path.startsWith('/shop') ||
            path.startsWith('/sales/') ||
            path.startsWith('/pos/affichage-client') ||
            path.startsWith('/login') ||
            path.startsWith('/signup');
          if (!isAuthFlow && !isExcludedPage && !ApiClient.sessionExpiredRedirected) {
            ApiClient.sessionExpiredRedirected = true;
            logger.warn('Session expirée — redirection vers /login');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Méthode pour définir le tenant ID courant
  setTenantId(tenantId: string | null) {
    logger.log('[ApiClient] Setting tenant ID:', tenantId);
    this.currentTenantId = tenantId;
  }

  // Méthodes génériques pour accès direct (utilisées par les adapters)
  async get<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.client.delete(url, config);
    return response.data;
  }

  // Health Check
  // ── Contrôle visuel à la préparation (webcam de la balance) ─────────────
  async getControleVisuelConfig() {
    const response = await this.client.get('/preparation-control/config');
    return response.data;
  }

  /** Une capture de contrôle : verdict + mesure, photo facultative (JPEG). */
  async enregistrerControleVisuel(
    orderId: string,
    lineId: string,
    champs: Record<string, string | number | boolean | null | undefined>,
    photo?: Blob | null,
  ) {
    const fd = new FormData();
    for (const [k, v] of Object.entries(champs)) {
      if (v !== null && v !== undefined) fd.append(k, String(v));
    }
    if (photo) fd.append('photo', photo, 'controle.jpg');
    const response = await this.client.post(`/preparation-control/orders/${orderId}/lines/${lineId}`, fd);
    return response.data;
  }

  async healthCheck() {
    const response = await this.client.get('/health');
    return response.data;
  }

  // AI Parsing
  async uploadAndParse(file: File, supplierHint?: string): Promise<UploadParseResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (supplierHint) {
      formData.append('supplier_hint', supplierHint);
    }

    const response = await this.client.post('/deliveries/upload-and-parse', formData, {
      headers: {
      },
    });
    return response.data;
  }

  async getAIStatus() {
    const response = await this.client.get('/deliveries/ai-status');
    return response.data;
  }

  async getAIUsage() {
    const response = await this.client.get('/deliveries/ai-usage');
    return response.data;
  }

  async getJobStatus(jobId: string): Promise<JobStatusResponse | null> {
    const response = await this.client.get(`/deliveries/jobs/${jobId}`);
    return response.data;
  }

  async getQueueStatus() {
    const response = await this.client.get('/deliveries/queue/status');
    return response.data;
  }

  // Deliveries
  async listDeliveries(tenantId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await this.client.get('/deliveries', {
      params: {
        tenant_id: tenantId,
        ...params,
      },
    });
    return response.data as Delivery[];
  }

  // Fournisseurs connus, dédupliqués (pour le sélecteur à la réception)
  async getSuppliers(tenantId: string): Promise<string[]> {
    const response = await this.client.get('/deliveries/suppliers', {
      params: { tenant_id: tenantId },
    });
    return response.data as string[];
  }

  async getDelivery(tenantId: string, deliveryId: string) {
    const response = await this.client.get(`/deliveries/${deliveryId}`, {
      params: { tenant_id: tenantId },
    });
    return response.data as Delivery;
  }

  async getDeliveryLines(
    tenantId: string,
    deliveryId: string,
    status?: 'pending' | 'approved' | 'rejected'
  ) {
    const response = await this.client.get(`/deliveries/${deliveryId}/lines`, {
      params: {
        tenant_id: tenantId,
        status,
      },
    });
    return response.data as DeliveryLine[];
  }

  async approveLine(tenantId: string, lineId: string, updates?: Partial<DeliveryLine>) {
    const response = await this.client.patch(
      `/deliveries/lines/${lineId}/approve`,
      updates,
      {
        params: { tenant_id: tenantId },
      }
    );
    return response.data;
  }

  /**
   * Annuler la réception d'une ligne : supprime le lot créé et remet la ligne
   * « à traiter ». Refusé par le serveur si le lot a déjà vécu (rayon, vente,
   * inventaire, perte, rangement).
   */
  async undoLineApproval(tenantId: string, lineId: string): Promise<{
    success: boolean;
    line_id: string;
    deleted_lot_number: string | null;
    /** Fiche créée par la réception annulée, supprimée si elle était vierge. */
    deleted_product_name: string | null;
  }> {
    const response = await this.client.patch(
      `/deliveries/lines/${lineId}/undo`,
      {},
      { params: { tenant_id: tenantId } },
    );
    return response.data;
  }

  async rejectLine(tenantId: string, lineId: string, reason?: string) {
    const response = await this.client.patch(
      `/deliveries/lines/${lineId}/reject`,
      { reason },
      {
        params: { tenant_id: tenantId },
      }
    );
    return response.data;
  }

  async closeDelivery(tenantId: string, deliveryId: string) {
    const response = await this.client.patch(
      `/deliveries/${deliveryId}/close`,
      {},
      {
        params: { tenant_id: tenantId },
      }
    );
    return response.data;
  }

  /**
   * Vérifier les conflits potentiels avant d'appliquer une livraison
   */
  async checkDeliveryConflicts(_tenantId: string, deliveryId: string): Promise<DeliveryConflictCheck> {
    const response = await this.client.get(`/deliveries/${deliveryId}/check-conflicts`);
    return response.data;
  }

  /**
   * Appliquer une livraison au catalogue
   * @param force Si true, applique même s'il y a des conflits/warnings
   */
  async applyDelivery(_tenantId: string, deliveryId: string, force: boolean = false): Promise<DeliveryApplyResult> {
    const url = force
      ? `/deliveries/${deliveryId}/apply?force=true`
      : `/deliveries/${deliveryId}/apply`;
    const response = await this.client.post(url);
    return response.data;
  }

  async updateDelivery(tenantId: string, deliveryId: string, updates: Partial<Delivery>) {
    const response = await this.client.patch(
      `/deliveries/${deliveryId}`,
      updates,
      {
        params: { tenant_id: tenantId },
      }
    );
    return response.data as Delivery;
  }

  async deleteDelivery(_tenantId: string, deliveryId: string) {
    const response = await this.client.delete(`/deliveries/${deliveryId}`);
    return response.data;
  }

  async getLineProductMatches(lineId: string, producerName?: string): Promise<{
    matches: ProductSuggestion[];
  }> {
    const response = await this.client.get(
      `/deliveries/lines/${lineId}/product-matches`,
      { params: producerName ? { producer_name: producerName } : {} },
    );
    return response.data;
  }

  async selectProductForLine(lineId: string, matchedProductId: string | null, producerName?: string): Promise<void> {
    await this.client.patch(
      `/deliveries/lines/${lineId}/select-product`,
      { matched_product_id: matchedProductId, producer_name: producerName },
    );
  }

  // ═══════════════════════════════════════════════════════════
  // Flux ORDER — Parsing email & réception
  // ═══════════════════════════════════════════════════════════

  async parseEmailText(text: string, supplierHint?: string): Promise<{
    success: boolean;
    delivery_id: string;
    supplier: string;
    line_count: number;
    confidence_score: number;
  }> {
    const response = await this.client.post('/deliveries/parse-email-text', {
      text,
      supplier_hint: supplierHint,
    });
    return response.data;
  }

  async attachDocument(deliveryId: string, file: File): Promise<{
    success: boolean;
    delivery_id: string;
    document_url: string;
    document_filename: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post(
      `/deliveries/${deliveryId}/attach-document`,
      formData,
    );
    return response.data;
  }

  async receiveLine(lineId: string, data: {
    received_quantity: number;
    reception_status: string;
  }): Promise<{
    id: string;
    received_quantity: number | null;
    reception_status: string;
    ordered_quantity: number | null;
  }> {
    const response = await this.client.patch(
      `/deliveries/lines/${lineId}/receive`,
      data,
    );
    return response.data;
  }

  async receiveLineByBarcode(deliveryId: string, barcode: string): Promise<{
    id: string;
    raw_name: string;
    received_quantity: number | null;
    ordered_quantity: number | null;
    reception_status: string;
    matched_by: string;
  }> {
    const response = await this.client.get(
      `/deliveries/${deliveryId}/receive-by-barcode`,
      { params: { barcode } },
    );
    return response.data;
  }

  /**
   * Scan EAN sur une ligne sélectionnée — matching produit immédiat (code-barres prioritaire).
   * scan_outcome : 'matched' (1 produit lié) | 'multiple' (EAN partagé, choix requis) | 'new' (aucun produit).
   */
  async scanLineBarcode(deliveryId: string, lineId: string, barcode: string): Promise<any> {
    const response = await this.client.post(
      `/deliveries/${deliveryId}/lines/${lineId}/scan-barcode`,
      { barcode },
    );
    return response.data;
  }

  async uploadLineLabelPhoto(lineId: string, file: File): Promise<{
    id: string;
    label_photo_url: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post(
      `/deliveries/lines/${lineId}/label-photo`,
      formData,
    );
    return response.data;
  }

  async updateDeliveryLine(tenantId: string, lineId: string, updates: Partial<DeliveryLine>) {
    const response = await this.client.patch(
      `/deliveries/lines/${lineId}`,
      updates,
      {
        params: { tenant_id: tenantId },
      }
    );
    return response.data as DeliveryLine;
  }

  // Stock workflow: backroom → display
  async moveToDisplay(tenantId: string, productId: string, quantity: number) {
    const response = await this.client.post(
      `/deliveries/products/${productId}/move-to-display`,
      { quantity },
      {
        params: { tenant_id: tenantId },
      }
    );
    return response.data;
  }

  async getBackroomProducts(tenantId: string) {
    const response = await this.client.get('/deliveries/backroom/products', {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  async getAllProductsWithStock(tenantId: string) {
    const response = await this.client.get('/deliveries/stock/products', {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  // Tenants
  async provisionTenant(data: {
    businessName: string;
    businessType: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    plan?: 'trial' | 'starter' | 'business' | 'premium';
  }): Promise<{
    tenantId: string;
    slug: string;
    subdomain: string;
    dbName: string;
    ownerEmail: string;
  }> {
    const response = await this.client.post('/tenants/provision', data);
    return response.data;
  }

  async getTenant(tenantId: string) {
    const response = await this.client.get(`/tenants/${tenantId}`);
    return response.data;
  }

  async getTenantBySlug(slug: string) {
    // Cache-bust pour Safari (ajoute timestamp pour éviter cache navigateur)
    const cacheBust = `_t=${Date.now()}`;
    const response = await this.client.get(`/tenants/slug/${slug}?${cacheBust}`);
    return response.data;
  }

  async updateTenantConfig(tenantId: string, config: any) {
    const response = await this.client.patch(`/tenants/${tenantId}/config`, { config });
    return response.data;
  }

  // POS Settings
  async getPOSSettings(tenantId: string) {
    const response = await this.client.get(`/tenants/${tenantId}/pos-settings`);
    return response.data;
  }

  async updatePOSSettings(tenantId: string, settings: {
    pos_auto_logout_enabled?: boolean;
    pos_auto_logout_delay_seconds?: number;
    pos_default_offline_vendor_id?: string | null;
    pos_virtual_keyboard_enabled?: boolean;
  }) {
    const response = await this.client.put(`/tenants/${tenantId}/pos-settings`, settings);
    return response.data;
  }

  // Categories
  async getCategories() {
    const response = await this.client.get('/categories');
    return response.data;
  }

  async getCategoriesWithPaths() {
    const response = await this.client.get('/categories/with-paths');
    return response.data;
  }

  async getCategoryStats() {
    const response = await this.client.get('/categories/stats');
    return response.data;
  }

  async createCategory(data: {
    name: string;
    slug?: string;
    description?: string;
    icon?: string;
    parent_id?: string;
    active?: boolean;
  }) {
    const response = await this.client.post('/categories', data);
    return response.data;
  }

  async updateCategory(id: string, data: {
    name?: string;
    slug?: string;
    description?: string;
    icon?: string;
    parent_id?: string;
    active?: boolean;
  }) {
    const response = await this.client.put(`/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: string) {
    const response = await this.client.delete(`/categories/${id}`);
    return response.data;
  }

  // Products
  async getProducts(params?: {
    search?: string;
    category_id?: string;
    active?: boolean;
    is_deposit?: boolean;
    in_stock_only?: boolean;
    in_display_only?: boolean; // Pour POS: filtre par stock_display > 0 (lots en rayon)
    page?: number;
    limit?: number;
  }) {
    const response = await this.client.get('/products', { params });
    return response.data;
  }

  async getProduct(id: string) {
    const response = await this.client.get(`/products/${id}`);
    return response.data;
  }

  /** Recherche de précommandes pour le comptoir : numéro, nom de client ou téléphone. */
  async getPreorder(id: string) {
    const response = await this.client.get(`/preorders/${id}`);
    return response.data;
  }

  /**
   * Annuler une précommande. Ce n'est pas une suppression : le statut passe à
   * CANCELLED et la ligne reste consultable — le boulanger a été prévenu, la
   * trace doit rester. Le serveur refuse une précommande déjà remise ou déjà
   * annulée.
   */
  /**
   * Clôturer une précommande sans vente : le cas du RÉASSORT MAGASIN, dont le
   * pain part en rayon et se vend au détail. Encaisser la précommande n'aurait
   * pas de sens, et c'est l'encaissement qui la solde d'habitude — d'où des
   * réassorts qui restaient ouverts pour toujours.
   *
   * Emprunte le chemin qu'utilise déjà la caisse après une vente, pour qu'il
   * n'existe qu'une seule façon de marquer une précommande retirée.
   */
  async markPreorderPickedUp(id: string) {
    const response = await this.client.patch(`/orders/${id}/status`, {
      status: 'picked_up',
      type: 'preorder',
    });
    return response.data;
  }

  async cancelPreorder(id: string) {
    const response = await this.client.patch(`/preorders/${id}/cancel`, {});
    return response.data;
  }

  async searchPreorders(q: string) {
    const response = await this.client.get(`/preorders/search?q=${encodeURIComponent(q)}`);
    return response.data;
  }

  /** Clients déjà connus (historique des précommandes), pour l'autocomplétion du nom. */
  async suggestPreorderCustomers(
    q: string,
    limit = 8,
  ): Promise<PreorderCustomerSuggestion[]> {
    if (!q || q.trim().length < 2) return [];
    const response = await this.client.get('/preorders/customer-suggestions', {
      params: { q: q.trim(), limit },
    });
    return response.data;
  }

  async getProductByBarcode(barcode: string) {
    const response = await this.client.get(`/products/barcode/${barcode}`);
    return response.data;
  }

  /**
   * Suggérer des produits similaires pour l'autocomplétion
   */
  async suggestProducts(query: string, limit: number = 10): Promise<ProductSuggestion[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }
    const response = await this.client.get('/products/suggest', {
      params: { q: query, limit },
    });
    return response.data;
  }

  async bulkCreateProducts(products: any[]) {
    const response = await this.client.post('/products/bulk-create', { products });
    return response.data;
  }

  /**
   * Obtenir le top des ventes du jour (pour tri par popularité dans le POS)
   */
  async getTopSalesToday(limit: number = 50): Promise<Array<{ product_id: string; total_sold: number }>> {
    try {
      const response = await this.client.get('/products/top-sales', { params: { limit } });
      return response.data;
    } catch (error) {
      console.error('[API] Erreur getTopSalesToday:', error);
      return [];
    }
  }

  async createProduct(data: {
    name: string;
    code?: string;
    barcode?: string;
    description?: string;
    is_weighted?: boolean;
    unit?: string;
    tax_class?: string;
    image_url?: string;
    active?: boolean;
    supplier_name?: string;
    producer_name?: string;
    origin_country?: string;
    stock_quantity?: number;
    is_deposit?: boolean;
    deposit_product_id?: string;
    deposit_qty_per_unit?: string;
    category_id?: string;
    bio?: boolean;
    vegan?: boolean;
    organic_certifier?: string;
    expiry_date?: string;
    purchase_price_cents?: number;
    suggested_price_cents?: number;
  }) {
    const response = await this.client.post('/products', data);
    return response.data;
  }

  async updateProduct(id: string, data: {
    name?: string;
    code?: string;
    barcode?: string;
    description?: string;
    is_weighted?: boolean;
    unit?: string;
    tax_class?: string;
    image_url?: string;
    active?: boolean;
    supplier_name?: string;
    producer_name?: string;
    origin_country?: string;
    stock_quantity?: number;
    is_deposit?: boolean;
    deposit_product_id?: string;
    deposit_qty_per_unit?: string;
    category_id?: string;
    bio?: boolean;
    vegan?: boolean;
    organic_certifier?: string;
    expiry_date?: string;
    purchase_price_cents?: number;
    suggested_price_cents?: number;
  }) {
    const response = await this.client.patch(`/products/${id}`, data);
    return response.data;
  }

  async deleteProduct(id: string) {
    const response = await this.client.delete(`/products/${id}`);
    return response.data;
  }

  async updateProductPrice(id: string, amount_cents: number, currency: string = 'EUR') {
    const response = await this.client.patch(`/products/${id}/price`, {
      amount_cents,
      currency,
    });
    return response.data;
  }

  async adjustProductStock(id: string, quantity: number, operation: 'set' | 'add' | 'subtract' = 'set') {
    const response = await this.client.patch(`/products/${id}/stock`, {
      quantity,
      operation,
    });
    return response.data;
  }

  // POS
  async createSale(saleData: {
    tenant_id: string;
    vendor_id?: string;
    customer_id?: string;
    payment_method: 'cash' | 'card' | 'wallet' | 'mixed';
    lines: {
      product_id: string;
      product_name: string;
      quantity: number;
      unit_price_cents: number;
      tax_rate: number;
      is_return?: boolean;
    }[];
  }) {
    const response = await this.client.post('/pos/sales', saleData);
    return response.data;
  }

  async getSale(id: string) {
    const response = await this.client.get(`/pos/sales/${id}`);
    return response.data;
  }

  async getTodaySales() {
    const response = await this.client.get('/pos/sales/today');
    return response.data;
  }

  /**
   * Tracabilite d'une vente : lot d'origine de chaque ligne, et livraison dont
   * il venait. Charge A LA DEMANDE quand on deplie une vente.
   */
  async getSaleTraceability(saleId: string) {
    const response = await this.client.get(`/pos/sales/${saleId}/tracabilite`);
    return response.data;
  }

  async getSalesHistory(from: string, to: string, params?: { search?: string; status?: string; paymentMethod?: string; minCents?: number; maxCents?: number; limit?: number; offset?: number }) {
    const response = await this.client.get('/pos/sales/history', { params: { from, to, ...params } });
    return response.data;
  }

  async getPOSStats(params?: {
    startDate?: string;
    endDate?: string;
    vendorId?: string;
  }) {
    const response = await this.client.get('/pos/stats', { params });
    return response.data;
  }

  async cancelSale(id: string, reason: string) {
    const response = await this.client.patch(`/pos/sales/${id}/cancel`, { reason });
    return response.data;
  }

  // Vendors
  async listVendors(tenantId: string) {
    const response = await this.client.get('/vendors', {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  async getVendor(vendorId: string) {
    const response = await this.client.get(`/vendors/${vendorId}`, { timeout: SESSION_TIMEOUT_MS });
    return response.data;
  }

  async verifyVendorPin(vendorId: string, pin: string) {
    const response = await this.client.post(`/vendors/${vendorId}/verify-pin`, { pin }, { timeout: SESSION_TIMEOUT_MS });
    return response.data;
  }

  async acquireVendorSession(vendorId: string, workstationId: string, force: boolean = false, takeover: boolean = false) {
    const response = await this.client.post(`/vendors/${vendorId}/session/acquire`, {
      workstation_id: workstationId,
      force,
      takeover,
    }, { timeout: SESSION_TIMEOUT_MS });
    return response.data;
  }

  async pingVendorSession(vendorId: string, workstationId: string, paymentInProgress: boolean = false) {
    const response = await this.client.post(`/vendors/${vendorId}/session/ping`, {
      workstation_id: workstationId,
      payment_in_progress: paymentInProgress,
    }, { timeout: SESSION_TIMEOUT_MS });
    return response.data;
  }

  async saveVendorCart(vendorId: string, cartSnapshot: any, workstationId?: string) {
    const response = await this.client.patch(`/vendors/${vendorId}/session/cart`, {
      cart_snapshot: cartSnapshot,
      workstation_id: workstationId,
    }, { timeout: SESSION_TIMEOUT_MS });
    return response.data;
  }

  async releaseVendorSession(vendorId: string, workstationId: string) {
    const response = await this.client.delete(`/vendors/${vendorId}/session/release`, {
      data: {
        workstation_id: workstationId,
      },
      timeout: SESSION_TIMEOUT_MS,
    });
    return response.data;
  }

  async getVendorCart(vendorId: string) {
    const response = await this.client.get(`/vendors/${vendorId}/session/cart`);
    return response.data;
  }

  async createVendor(tenantId: string, data: {
    name: string;
    email: string | null;
    pin?: string;
    role: 'ADMIN' | 'MANAGER' | 'CASHIER' | 'CASHIER_PLUS' | 'FIELD';
  }) {
    const response = await this.client.post('/vendors', {
      tenant_id: tenantId,
      ...data,
    });
    return response.data;
  }

  async updateVendor(vendorId: string, data: Partial<{
    name: string;
    email: string | null;
    pin: string;
    role: 'ADMIN' | 'MANAGER' | 'CASHIER' | 'CASHIER_PLUS' | 'FIELD';
    active: boolean;
    display_order: number;
  }>) {
    const response = await this.client.patch(`/vendors/${vendorId}`, data);
    return response.data;
  }

  // ============================================================================
  // ORDERS (Commandes E-commerce - Admin)
  // ============================================================================

  async getOrdersAdmin(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await this.client.get('/orders/admin', { params });
    return response.data;
  }

  async getOrderAdmin(orderId: string) {
    const response = await this.client.get(`/orders/${orderId}`);
    return response.data;
  }

  async updateOrderStatus(orderId: string, status: string) {
    const response = await this.client.patch(`/orders/${orderId}/status`, { status });
    return response.data;
  }

  // ============================================================================
  // REFUNDS (Remboursements)
  // ============================================================================

  async getRefundsForOrder(orderId: string) {
    const response = await this.client.get(`/refunds/order/${orderId}`);
    return response.data;
  }

  // ============================================================================
  // ANALYTICS
  // ============================================================================

  async getSalesOverview(from: string, to: string) {
    const response = await this.client.get(`/analytics/sales/overview?from=${from}&to=${to}`);
    return response.data;
  }

  async getB2bOverview(from: string, to: string) {
    const response = await this.client.get(`/analytics/b2b/overview?from=${from}&to=${to}`);
    return response.data;
  }

  async getSalesByPeriod(from: string, to: string, granularity: 'hour' | 'day' | 'week' | 'month' = 'day') {
    const response = await this.client.get(`/analytics/sales/by-period?from=${from}&to=${to}&granularity=${granularity}`);
    return response.data;
  }

  async getTopProducts(from: string, to: string, limit: number = 10) {
    const response = await this.client.get(`/analytics/sales/top-products?from=${from}&to=${to}&limit=${limit}`);
    return response.data;
  }

  async getSalesByVendor(from: string, to: string) {
    const response = await this.client.get(`/analytics/sales/by-vendor?from=${from}&to=${to}`);
    return response.data;
  }

  async getCategoryBreakdown(from: string, to: string) {
    const response = await this.client.get(`/analytics/sales/by-category?from=${from}&to=${to}`);
    return response.data;
  }

  async getOwnProductionSales(from: string, to: string, rayon?: string) {
    const params = new URLSearchParams({ from, to });
    if (rayon) params.set('rayon', rayon);
    const response = await this.client.get(`/analytics/sales/own-production?${params}`);
    return response.data;
  }

  async getResaleMargins(from: string, to: string, rayon?: string, supplier?: string) {
    const params = new URLSearchParams({ from, to });
    if (rayon) params.set('rayon', rayon);
    if (supplier) params.set('supplier', supplier);
    const response = await this.client.get(`/analytics/margins/resale?${params}`);
    return response.data;
  }

  async getMarginsTimeline(from: string, to: string, granularity?: 'day' | 'week' | 'month') {
    const params = new URLSearchParams({ from, to });
    if (granularity) params.set('granularity', granularity);
    const response = await this.client.get(`/analytics/margins/timeline?${params}`);
    return response.data;
  }

  async getMarginAnalysis(from: string, to: string) {
    const response = await this.client.get(`/analytics/margins?from=${from}&to=${to}`);
    return response.data;
  }

  async getProductMargins(from: string, to: string, categoryId?: string) {
    const params = new URLSearchParams({ from, to });
    if (categoryId) params.append('categoryId', categoryId);
    const response = await this.client.get(`/analytics/margins/products?${params}`);
    return response.data;
  }

  async updateProductPurchasePrice(productId: string, purchasePriceCents: number) {
    const response = await this.client.patch(`/analytics/margins/products/${productId}/purchase-price`, {
      purchase_price_cents: purchasePriceCents,
    });
    return response.data;
  }

  async getInventoryOverview() {
    const response = await this.client.get('/analytics/inventory/overview');
    return response.data;
  }

  async getExpiringProducts(days: number = 7) {
    const response = await this.client.get(`/analytics/inventory/expiring?days=${days}`);
    return response.data;
  }

  async searchCustomers(query: string, limit: number = 10) {
    const response = await this.client.get('/customers/search', {
      params: { query, limit },
    });
    return response.data;
  }

  async listCustomers(page: number = 1, limit: number = 50) {
    const response = await this.client.get('/customers', { params: { page, limit } });
    return response.data;
  }

  async getCustomer(id: string) {
    const response = await this.client.get(`/customers/${id}`);
    return response.data;
  }

  async createCustomer(data: { email?: string; first_name: string; last_name: string; phone?: string; postal_address?: string; vat_number?: string }) {
    const response = await this.client.post('/customers', data);
    return response.data;
  }

  async updateCustomer(id: string, data: { email?: string; first_name?: string; last_name?: string; phone?: string; postal_address?: string; vat_number?: string; active?: boolean }) {
    const response = await this.client.patch(`/customers/${id}`, data);
    return response.data;
  }

  /**
   * Adresse publique signée d'une commande, pour le QR du ticket client.
   * `null` en cas d'échec : le ticket s'imprime alors sans QR, jamais bloqué.
   */
  async lienClientCommande(orderId: string): Promise<string | null> {
    try {
      const response = await this.client.get(`/orders/admin/${orderId}/lien-client`);
      return response.data?.url || null;
    } catch {
      return null;
    }
  }

  /** Chrono de préparation : « pause » ou « reprise » (rentabilité, 26/10/2026). */
  async chronoPreparation(orderId: string, action: 'pause' | 'reprise') {
    const response = await this.client.patch(`/orders/${orderId}/chrono`, { action });
    return response.data;
  }

  /** Colis faits à « Commande prête » (26/10/2026) : liste avec zone et commentaire. */
  async noterColis(orderId: string, colis: Array<{ zone: string; commentaire?: string }>) {
    const response = await this.client.patch(`/orders/${orderId}/colis`, { colis_detail: colis });
    return response.data;
  }

  /** Autoriser (ou retirer) le paiement au retrait pour un client (19/10/2026). */
  async definirPaiementAuRetrait(customerId: string, autorise: boolean) {
    const response = await this.client.patch(`/customers/${customerId}/paiement-au-retrait`, { autorise });
    return response.data;
  }

  /** Déjà client du magasin (true), nouveau venu par la boutique en ligne (false), ou pas encore dit (null). */
  async definirClientMagasin(customerId: string, clientMagasin: boolean | null) {
    const response = await this.client.patch(`/customers/${customerId}/client-magasin`, { client_magasin: clientMagasin });
    return response.data;
  }

  async getWalletBalance(customerId: string) {
    const response = await this.client.get(`/wallet/balance/${customerId}`);
    return response.data;
  }

  async creditCustomerWallet(customerId: string, amountCents: number, reference?: string) {
    const response = await this.client.post(`/wallet/credit/${customerId}`, {
      amount_cents: amountCents,
      reference: reference || `Crédit POS ${new Date().toISOString().slice(0, 10)}`,
    });
    return response.data;
  }

  async getCustomerOverview() {
    const response = await this.client.get('/analytics/customers/overview');
    return response.data;
  }

  async getDeliveryOverview() {
    const response = await this.client.get('/analytics/deliveries/overview');
    return response.data;
  }

  async getOperationalKPIs() {
    const response = await this.client.get('/analytics/operational');
    return response.data;
  }

  // E-Commerce Analytics

  async getEcomOverview(from: string, to: string) {
    const response = await this.client.get('/analytics/ecom/overview', { params: { from, to } });
    return response.data;
  }

  async getEcomByPeriod(from: string, to: string, granularity: string = 'day') {
    const response = await this.client.get('/analytics/ecom/by-period', { params: { from, to, granularity } });
    return response.data;
  }

  async getEcomTopProducts(from: string, to: string, limit: number = 10) {
    const response = await this.client.get('/analytics/ecom/top-products', { params: { from, to, limit } });
    return response.data;
  }

  async getEcomCategoryBreakdown(from: string, to: string) {
    const response = await this.client.get('/analytics/ecom/by-category', { params: { from, to } });
    return response.data;
  }

  // ============================================================================
  // LIVE SHOPPING
  // ============================================================================

  /**
   * Clôturer une session Live Shopping abandonnée
   * Utilisé par le vendeur pour nettoyer les sessions "zombies"
   */
  async closeAbandonedSession(sessionId: string): Promise<{ success: boolean; sessionId: string }> {
    const response = await this.client.patch(`/live-shopping/vendor/sessions/${sessionId}/close`);
    return response.data;
  }

  // ============================================================================
  // VENDOR WORK DRAFTS (Auto-save pour contextes POS/Préparation/Live)
  // ============================================================================

  /**
   * Sauvegarder le brouillon de travail d'un vendeur
   * @param vendorId ID du vendeur
   * @param context Contexte de travail ('pos', 'preparation', 'live_shopping')
   * @param cartSnapshot Snapshot du panier/état courant
   * @param orderId (Optionnel) ID de la commande en cours de préparation
   * @param metadata (Optionnel) Métadonnées additionnelles
   */
  async saveVendorDraft(
    vendorId: string,
    context: 'pos' | 'preparation' | 'live_shopping',
    cartSnapshot: any,
    orderId?: string,
    metadata?: any
  ): Promise<{
    success: boolean;
    draft: {
      id: string;
      context: string;
      saved_at: string;
      items_count: number;
    };
  }> {
    const response = await this.client.post(`/vendor-drafts/${vendorId}/${context}`, {
      cart_snapshot: cartSnapshot,
      order_id: orderId,
      metadata,
    });
    return response.data;
  }

  /**
   * Récupérer le brouillon de travail d'un vendeur pour un contexte donné
   * @param vendorId ID du vendeur
   * @param context Contexte de travail ('pos', 'preparation', 'live_shopping')
   */
  async getVendorDraft(
    vendorId: string,
    context: 'pos' | 'preparation' | 'live_shopping'
  ): Promise<{
    id: string;
    vendor_id: string;
    context: string;
    order_id: string | null;
    cart_snapshot: any;
    metadata: any;
    saved_at: string;
    updated_at: string;
  } | null> {
    try {
      const response = await this.client.get(`/vendor-drafts/${vendorId}/${context}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Supprimer le brouillon de travail d'un vendeur pour un contexte donné
   * @param vendorId ID du vendeur
   * @param context Contexte de travail ('pos', 'preparation', 'live_shopping')
   */
  async deleteVendorDraft(
    vendorId: string,
    context: 'pos' | 'preparation' | 'live_shopping'
  ): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/vendor-drafts/${vendorId}/${context}`);
    return response.data;
  }

  /**
   * Lister tous les brouillons d'un vendeur (tous contextes)
   * @param vendorId ID du vendeur
   */
  async listVendorDrafts(vendorId: string): Promise<Array<{
    id: string;
    context: string;
    saved_at: string;
    items_count: number;
  }>> {
    const response = await this.client.get(`/vendor-drafts/${vendorId}`);
    return response.data;
  }

  // ============================================================================
  // UPLOADS / S3
  // ============================================================================

  /**
   * Extraire le nom de fichier depuis une URL S3
   * @param url URL complète S3 (ex: https://s3.gra.io.cloud.ovh.net/folies-uploads/tenant-id/images/filename.jpg)
   * @returns Le nom de fichier ou null si URL invalide
   */
  extractFilenameFromS3Url(url: string): string | null {
    if (!url) return null;
    try {
      // L'URL S3 a le format: https://endpoint/bucket/tenant-id/images/filename.jpg
      // ou: https://endpoint/bucket/tenant-id/thumbnails/thumb-filename.jpg
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      // Le filename est le dernier élément du path
      const filename = pathParts[pathParts.length - 1];
      return filename || null;
    } catch {
      return null;
    }
  }

  /**
   * Supprimer une image de S3
   * @param filename Nom du fichier à supprimer (ex: "abc123.jpg")
   * @returns Promise<{ success: boolean; message: string }>
   */
  async deleteImage(filename: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/uploads/image/${encodeURIComponent(filename)}`);
    return response.data;
  }

  /**
   * Supprimer une image depuis son URL S3 complète
   * @param url URL complète S3
   * @returns Promise<{ success: boolean; message: string }> ou null si URL invalide
   */
  async deleteImageByUrl(url: string): Promise<{ success: boolean; message: string } | null> {
    const filename = this.extractFilenameFromS3Url(url);
    if (!filename) {
      logger.warn('[ApiClient] Cannot extract filename from URL:', url);
      return null;
    }
    return this.deleteImage(filename);
  }

  // ============================================================================
  // IMAGES PRODUITS (multi-images)
  // ============================================================================

  async getProductImages(productId: string): Promise<ProductImage[]> {
    const response = await this.client.get(`/products/${productId}/images`);
    return response.data;
  }

  async uploadProductImage(
    productId: string,
    file: File,
    type: 'PRODUCT' | 'LABEL' = 'PRODUCT',
  ): Promise<ProductImage> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await this.client.post(`/products/${productId}/images`, formData, {
    });
    return response.data;
  }

  async deleteProductImage(productId: string, imageId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/products/${productId}/images/${imageId}`);
    return response.data;
  }

  async reorderProductImages(productId: string, imageIds: string[]): Promise<{ message: string }> {
    const response = await this.client.patch(`/products/${productId}/images/reorder`, { image_ids: imageIds });
    return response.data;
  }

  /** Pivoter/recadrer une image (transformation serveur, nouvelle URL). */
  async transformProductImage(
    productId: string,
    imageId: string,
    params: {
      rotate?: number;
      crop?: { x: number; y: number; width: number; height: number };
      white_point?: boolean;
      white_point_at?: { x: number; y: number };
      /** Force de la correction pipette (0.1 = à peine, 1 = le point devient blanc pur). */
      white_point_strength?: number;
      /** Zones à effacer (fractions, après rotation) — remplies avec la couleur du pourtour. */
      erase?: Array<{ x: number; y: number; width: number; height: number }>;
      /** Détourer le sujet et le poser sur du blanc (microservice de retouche). */
      cutout?: boolean;
      /** Contour détouré, en pixels : négatif = rogné, positif = élargi. */
      cutout_margin?: number;
      /** Fondu du bord détouré, en pixels (0 = net, 2-3 = fondu dans le blanc). */
      cutout_feather?: number;
      /** Ombre sous le sujet détouré : aucune, portée, ou au sol. */
      cutout_shadow?: 'none' | 'portee' | 'sol';
      /** Intensité de l'ombre, 0-100. */
      cutout_shadow_strength?: number;
    },
  ): Promise<ProductImage> {
    const response = await this.client.patch(`/products/${productId}/images/${imageId}/transform`, params);
    return response.data;
  }

  /**
   * Aperçu d'un détourage, sans rien enregistrer — le curseur de contour se règle
   * en voyant le résultat. Part toujours de la même source, ce qui permet au
   * microservice de retrouver son masque : moins d'une seconde par ajustement.
   */
  async previewProductImageCutout(
    productId: string,
    imageId: string,
    margin: number,
    feather: number,
    shadow: 'none' | 'portee' | 'sol' = 'none',
    shadowStrength = 50,
  ): Promise<{ image_base64: string }> {
    const response = await this.client.post(
      `/products/${productId}/images/${imageId}/cutout-preview`,
      { margin, feather, shadow, shadow_strength: shadowStrength },
    );
    return response.data;
  }

  /**
   * Repère les dates et prix imprimés sur une photo, avec leur position, pour
   * pré-tracer les zones à masquer. Côté serveur : marche depuis l'iPad comme
   * depuis un Mac mini.
   */
  async productImageTextZones(
    productId: string,
    imageId: string,
  ): Promise<{
    zones: Array<{ text: string; confidence: number; x: number; y: number; width: number; height: number; kind: string }>;
    engine: string;
  }> {
    const response = await this.client.post(`/products/${productId}/images/${imageId}/text-zones`);
    return response.data;
  }

  /** Pipette : couleur d'un point de l'image + multiplicateurs vers le blanc (aperçu). */
  async sampleProductImageColor(
    productId: string,
    imageId: string,
    x: number,
    y: number,
  ): Promise<{ color: { r: number; g: number; b: number }; multipliers: { r: number; g: number; b: number } }> {
    const response = await this.client.get(`/products/${productId}/images/${imageId}/sample`, { params: { x, y } });
    return response.data;
  }

  /** Requalifier une image (produit ↔ étiquette). */
  async updateProductImage(
    productId: string,
    imageId: string,
    params: { type?: 'PRODUCT' | 'LABEL'; alt_text?: string },
  ): Promise<ProductImage> {
    const response = await this.client.patch(`/products/${productId}/images/${imageId}`, params);
    return response.data;
  }

  // ============================================================================
  // PICKUP / RETRAIT EN MAGASIN
  // ============================================================================

  /**
   * Rechercher une commande par son numéro (scan code-barres)
   * @param orderNumber Numéro de commande (ex: CMD-20241210-001)
   */
  async lookupOrderByNumber(orderNumber: string): Promise<{
    id: string;
    order_number: string;
    customer_name: string;
    customer_email: string;
    customer_phone?: string;
    status: string;
    payment_status: string;
    delivery_option: string;
    delivery_date?: string;
    delivery_slot?: string;
    total_cents: number;
    lines: Array<{
      id: string;
      product_name: string;
      quantity: number;
      unit_price_cents: number;
      total_price_cents: number;
      product?: {
        id: string;
        name: string;
        is_weighted: boolean;
        unit?: string;
      };
    }>;
    prepared_at?: string;
    prepared_by?: { id: string; name: string };
    delivered_at?: string;
    delivered_by?: string;
    customer?: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      phone?: string;
    };
  } | null> {
    try {
      const response = await this.client.get(`/orders/lookup/${encodeURIComponent(orderNumber)}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Récupérer les commandes pour retrait en magasin
   * @param search Recherche optionnelle par nom, email, téléphone ou numéro de commande
   * @param pickupStatus Filtre par statut: 'pending' (non reprises), 'completed' (reprises), 'all' (toutes)
   */
  async getReadyForPickup(
    search?: string,
    pickupStatus: 'pending' | 'completed' | 'all' = 'pending',
  ): Promise<Array<{
    id: string;
    order_number: string;
    customer_name: string;
    customer_email: string;
    customer_phone?: string;
    delivery_option: string;
    delivery_date?: string;
    delivery_slot?: string;
    status: string;
    payment_status: string;
    total_cents: number;
    lines: Array<{
      id: string;
      product_name: string;
      quantity: number;
      unit_price_cents: number;
      total_price_cents: number;
      product?: { id: string; name: string; is_weighted: boolean };
    }>;
    prepared_at?: string;
    prepared_by?: { id: string; name: string };
    delivered_at?: string;
    delivered_by?: string;
    customer?: { id: string; email: string; first_name: string; last_name: string; phone?: string };
    created_at: string;
  }>> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (pickupStatus) params.append('pickupStatus', pickupStatus);
    const queryString = params.toString();
    const response = await this.client.get(`/orders/ready-for-pickup${queryString ? `?${queryString}` : ''}`);
    return response.data;
  }

  /**
   * Finaliser le retrait d'une commande en magasin
   * @param orderId ID de la commande
   * @param vendorId ID du vendeur qui remet la commande
   * @param vendorName Nom du vendeur (optionnel)
   * @param notes Notes additionnelles (optionnel)
   */
  /**
   * Reprise des vidanges d'une commande en ligne : recomptées au comptoir,
   * créditées en FOLIES. Une seule fois par commande (le serveur renvoie la
   * reprise existante au second appel).
   */
  async repriseVidanges(
    orderId: string,
    lignes: Array<{ product_id: string; quantity: number }>,
    vendorId?: string | null,
    vendorName?: string | null,
  ): Promise<{ deja: boolean; reprise: { total_cents: number; sale_number: string | null } }> {
    const response = await this.client.post(`/pos/ecom-orders/${orderId}/vidanges`, {
      lignes,
      vendor_id: vendorId || undefined,
      vendor_name: vendorName || undefined,
    });
    return response.data;
  }

  async pickupHandover(
    orderId: string,
    vendorId: string,
    vendorName?: string,
    notes?: string
  ): Promise<{
    success: boolean;
    order: any;
    handover: {
      delivered_at: string;
      delivered_by: string;
      vendor_id: string;
    };
  }> {
    const response = await this.client.post(`/orders/${orderId}/pickup-handover`, {
      vendor_id: vendorId,
      vendor_name: vendorName,
      notes,
    });
    return response.data;
  }

  // ============================================================================
  // WORKSTATIONS
  // ============================================================================

  /**
   * Récupère toutes les workstations du tenant
   */
  async getWorkstations(includeInactive = false): Promise<any[]> {
    const response = await this.client.get('/workstations', {
      params: { includeInactive },
    });
    return response.data;
  }

  /**
   * Récupère les codes de workstations standardisés
   */
  async getWorkstationCodes(): Promise<Record<string, string>> {
    const response = await this.client.get('/workstations/codes');
    return response.data;
  }

  /**
   * Initialise les workstations par défaut (seed)
   */
  async seedWorkstations(): Promise<{ created: string[]; skipped: string[]; total: number }> {
    const response = await this.client.post('/workstations/seed');
    return response.data;
  }

  /**
   * Récupère les workstations assignées à un vendor
   */
  async getVendorWorkstations(vendorId: string): Promise<{
    workstation: any;
    can_execute: boolean;
    can_supervise: boolean;
  }[]> {
    const response = await this.client.get(`/workstations/vendors/${vendorId}/workstations`);
    return response.data;
  }

  /**
   * Récupère uniquement les codes des workstations d'un vendor (pour filtrage UI)
   */
  async getVendorWorkstationCodes(vendorId: string): Promise<string[]> {
    const response = await this.client.get(`/workstations/vendors/${vendorId}/codes`);
    return response.data;
  }

  /**
   * Met à jour toutes les workstations d'un vendor (remplace les existantes)
   */
  async setVendorWorkstations(
    vendorId: string,
    workstationCodes: string[],
    options?: { can_execute?: boolean; can_supervise?: boolean }
  ): Promise<{ vendor_id: string; workstations: string[]; count: number }> {
    const response = await this.client.put(`/workstations/vendors/${vendorId}/workstations`, {
      workstationCodes,
      ...options,
    });
    return response.data;
  }

  /**
   * Assigne une workstation à un vendor
   */
  async assignWorkstationToVendor(
    vendorId: string,
    workstationId: string,
    options?: { can_execute?: boolean; can_supervise?: boolean }
  ): Promise<any> {
    const response = await this.client.post(
      `/workstations/vendors/${vendorId}/assign/${workstationId}`,
      options || {}
    );
    return response.data;
  }

  /**
   * Retire une workstation d'un vendor
   */
  async removeWorkstationFromVendor(vendorId: string, workstationId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/workstations/vendors/${vendorId}/assign/${workstationId}`);
    return response.data;
  }

  /**
   * Récupère les vendors assignés à une workstation
   */
  async getWorkstationVendors(workstationId: string): Promise<{
    vendor: any;
    can_execute: boolean;
    can_supervise: boolean;
  }[]> {
    const response = await this.client.get(`/workstations/${workstationId}/vendors`);
    return response.data;
  }

  // ==================== Product Recognition (Caméra IA) ====================

  /**
   * Classifier un produit via image caméra (Claude Vision API)
   */
  async classifyProduct(
    imageBase64: string,
    mimeType: string,
    weight?: number,
  ): Promise<{
    suggestions: Array<{
      name: string;
      category: string;
      confidence: number;
      variety?: string;
      description?: string;
      matchedProductId?: string;
      matchedProductName?: string;
      matchScore?: number;
    }>;
    durationMs: number;
  }> {
    const response = await this.client.post('/product-recognition/classify', {
      image: imageBase64,
      mimeType,
      weight,
    });
    return response.data;
  }

  /**
   * Extraire DLC + n° de lot d'une photo d'étiquette (réception fournisseur).
   * Pré-remplissage à confirmer par l'opérateur.
   */
  async extractLabelInfo(
    imageBase64: string,
    mimeType: string,
  ): Promise<{
    dlc: string | null;
    lot_number: string | null;
    date_type: 'DLC' | 'DDM' | null;
    confidence: number;
  }> {
    const response = await this.client.post('/product-recognition/extract-label', {
      image: imageBase64,
      mimeType,
    });
    return response.data;
  }

  // ── Reconnaissance F&L "qui apprend" : index d'empreintes partagé ──────────────
  async getProduceEmbeddings(
    since?: string,
  ): Promise<{
    serverTime: string;
    count: number;
    embeddings: Array<{ id: string; product_id: string; embedding: string; dims: number; created_at: string }>;
  }> {
    const response = await this.client.get('/produce-vision/embeddings', {
      params: since ? { since } : {},
    });
    return response.data;
  }

  async addProduceEmbedding(payload: {
    product_id: string;
    embedding: string;
    dims?: number;
    source?: string;
  }): Promise<{ id: string; kept: number }> {
    const response = await this.client.post('/produce-vision/embeddings', payload);
    return response.data;
  }
}

export const api = new ApiClient();
export default api;
